import cx from 'classnames';
import { useEffect } from 'react';
import { FormFieldInputType, ValidationActionType } from 'models/enum';
import { ChartActionsComponentProps, ChartMetaFormField } from 'models/ui';
import { ChartActions, ChartPermission, ErrorCard, ErrorFormField, SectionDivider } from 'components/common';
import { ELSButton, ELSFlex, ELSFlexItem } from 'components/common/els';
import ErrorTooltip from 'components/common/error-form-field/ErrorTooltip';
import { CHAR_LIMIT, FormField, MULTISELECT_NONE_EXCLUSIVE_FIELDS, OTHER_FIELD_DEPENDENCIES, SELF_NAME_FIELD_DEPENDENCIES, SectionTitle } from './constants';
import NestedMultiselectDropdown from './NestedMultiselectDropdown';
import SelectMultiselectDropdown from './SelectMultiselectDropdown';
import './discharge-planning.scss';

interface DischargePlanningViewProps {
  chartActionsProps: ChartActionsComponentProps;
  formFieldMap: Map<string, ChartMetaFormField>;
  formSubmittedCount: number;
  multiselectResetKey: number;
  isReadOnly?: boolean;
  isAuthor?: boolean;
}

const SELECT_INPUT_PROPS = { placeholder: 'Select' };

const hasFieldValue = (formField: ChartMetaFormField | undefined): boolean => {
  if (!formField) return false;
  const { value } = formField;
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && `${value}` !== '';
};

const DischargePlanningView = ({ chartActionsProps, formFieldMap, formSubmittedCount, multiselectResetKey, isReadOnly = false, isAuthor = false }: DischargePlanningViewProps) => {
  const isOptionSelected = (formField: FormField, optionLabel: string): boolean => {
    const field = formFieldMap.get(formField);
    const chartContent = field?.chartContent ?? [];
    const noneSelected = chartContent.some((c) => c.selected && c.label === 'None');
    if (optionLabel !== 'None' && noneSelected && MULTISELECT_NONE_EXCLUSIVE_FIELDS.has(formField)) {
      return false;
    }
    return !!chartContent.some((c) => c.selected && c.label === optionLabel);
  };

  const updateFieldValue = (formField: FormField, value: string) => {
    const field = formFieldMap.get(formField);
    if (!field?.onChange || field.value === value) return;
    field.onChange(field, { target: { value } });
  };

  useEffect(() => {
    OTHER_FIELD_DEPENDENCIES.forEach(({ parent, other }) => {
      if (!isOptionSelected(parent, 'Other')) {
        updateFieldValue(other, '');
      }
    });

    SELF_NAME_FIELD_DEPENDENCIES.forEach(({ parent, name }) => {
      if (isOptionSelected(parent, 'Self')) {
        updateFieldValue(name, '');
      }
    });

    if (isOptionSelected(FormField.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL, 'None')) {
      updateFieldValue(FormField.OTHER_MOTIVATIONAL_OBSERVATIONS, '');
    }
  }, [formFieldMap]);

  const getFieldByMode = (formField: FormField): ChartMetaFormField => {
    const field = formFieldMap.get(formField);
    if (!field || !isReadOnly || field.disabled) {
      return field;
    }

    return {
      ...field,
      disabled: true,
      chartContent: field.chartContent?.map((item) => ({
        ...item,
        disabled: true
      }))
    };
  };

  const applyExclusiveNone = (field: ChartMetaFormField): ChartMetaFormField => {
    if (!field?.chartContent) return field;
    const noneSelected = field.chartContent.some((c) => c.selected && c.label === 'None');
    if (!noneSelected) {
      return {
        ...field,
        chartContent: field.chartContent.map((c) => ({ ...c, isHidden: false }))
      };
    }
    return {
      ...field,
      chartContent: field.chartContent.map((c) => (c.label === 'None' ? c : { ...c, isHidden: true }))
    };
  };

  const renderField = (formField: FormField, inputProps?: { [x: string]: any }) => (
    <ErrorFormField formField={getFieldByMode(formField)} formSubmittedCount={formSubmittedCount} inputProps={inputProps} />
  );

  const withCharLimit = (field: ChartMetaFormField): ChartMetaFormField => {
    if (!field?.onChange) return field;
    return {
      ...field,
      onChange: (f: ChartMetaFormField, arg: unknown, ...rest: unknown[]) => {
        const nextValue = String((arg as { target?: { value?: unknown } })?.target?.value ?? '');
        const trimmedArg =
          nextValue.length <= CHAR_LIMIT ? arg : { ...(arg as Record<string, unknown>), target: { ...((arg as any)?.target ?? {}), value: nextValue.slice(0, CHAR_LIMIT) } };
        field.onChange(f, trimmedArg, ...rest);
      }
    };
  };

  const renderTextareaField = (formField: FormField, inputProps?: { [x: string]: any }) => {
    const field = getFieldByMode(formField);
    return (
      <div className="discharge-planning__textarea-wrap">
        <ErrorFormField formField={withCharLimit(field)} formSubmittedCount={formSubmittedCount} inputProps={inputProps} />
        <div className="discharge-planning__char-count">
          {String(field?.value ?? '').length}/{CHAR_LIMIT} characters
        </div>
      </div>
    );
  };

  const renderSelectField = (formField: FormField) => {
    const chartFormField = getFieldByMode(formField);
    if (!chartFormField) return null;

    return (
      <div className={cx({ 'discharge-planning__dropdown-placeholder': !hasFieldValue(chartFormField) })}>
        <ErrorFormField formField={chartFormField} formSubmittedCount={formSubmittedCount} inputProps={SELECT_INPUT_PROPS} />
      </div>
    );
  };

  const renderCustomMultiselectField = (fieldKey: FormField, renderInput: (formField: ChartMetaFormField) => JSX.Element, resetKey?: string) => {
    const formField = getFieldByMode(fieldKey);
    if (!formField) return null;

    const errors = formSubmittedCount > 0 ? formField.errors : formField.errors?.filter((e) => e?.type !== ValidationActionType.REQUIRED);
    const hasErrors = errors?.length > 0;
    const showTooltip = hasErrors && errors.some((err) => !!err.message);

    return (
      <div
        key={resetKey}
        id={`chart-field-${formField.name}`}
        className={cx(`chart-input__field chart-field-${formField.name}`, 'u-els-margin-bottom-2x', {
          'chart-input__field--has-errors': hasErrors
        })}
      >
        <div id={`chart-field-label-${formField.name}`} className="chart-input__label">
          <label>
            {formField.isRequired && <span className="chart-input__label-asterisk">* </span>}
            {formField.label}
          </label>
        </div>
        {renderInput(formField)}
        {showTooltip && <ErrorTooltip id={`chart-field-errors-${formField.name}`} errors={errors} formSubmittedCount={formSubmittedCount} />}
      </div>
    );
  };

  const renderSelectMultiselectField = (fieldKey: FormField, resetKey?: string) =>
    getFieldByMode(fieldKey)?.inputType === FormFieldInputType.MULTISELECT_DROPDOWN
      ? renderCustomMultiselectField(
          fieldKey,
          (formField) => (
            <div className="chart-input__multi-select-dropdown">
              <SelectMultiselectDropdown chartFormField={applyExclusiveNone(formField)} ariaRequired={formField.isRequired} />
            </div>
          ),
          resetKey
        )
      : null;

  const renderNestedMultiselectField = () =>
    renderCustomMultiselectField(
      FormField.NEEDS_BEFORE_DISCHARGE,
      (formField) => <NestedMultiselectDropdown chartFormField={applyExclusiveNone(formField)} ariaRequired={formField.isRequired} />,
      `${FormField.NEEDS_BEFORE_DISCHARGE}-${multiselectResetKey}`
    );

  const renderNameField = (parentField: FormField, nameField: FormField) => {
    const field = getFieldByMode(nameField);
    if (!field) return null;
    const selfSelected = isOptionSelected(parentField, 'Self');
    const overridden = selfSelected ? { ...field, disabled: true, isRequired: false, value: '' } : field;

    return (
      <div className="discharge-planning__name-field">
        <ErrorFormField formField={withCharLimit(overridden)} formSubmittedCount={formSubmittedCount} />
      </div>
    );
  };

  const renderOtherTextInput = (parentField: FormField, otherField: FormField) => {
    if (!isOptionSelected(parentField, 'Other')) return null;
    return renderField(otherField);
  };

  const renderChartActions = (allowSaveFragment: boolean) => {
    if (isReadOnly || !allowSaveFragment) {
      return null;
    }

    if (isAuthor) {
      return (
        <ELSFlex className="sng-form-buttons" left wrap>
          {chartActionsProps.onSaveClick && (
            <ELSFlexItem>
              <ELSButton text="Save Records" isDisabled={!chartActionsProps.enableSaveButton} onClick={chartActionsProps.onSaveClick} />
            </ELSFlexItem>
          )}
          {chartActionsProps.onDisplayRecordsClick && (
            <ELSFlexItem>
              <ELSButton type="secondary" text="Return with data" isDisabled={!chartActionsProps.enableDisplayRecordsButton} onClick={chartActionsProps.onDisplayRecordsClick} />
            </ELSFlexItem>
          )}
        </ELSFlex>
      );
    }

    return <ChartActions {...chartActionsProps} />;
  };

  return (
    <div className="discharge-planning">
      <h1 className="u-els-margin-bottom-2x">{SectionTitle.DISCHARGE_PLANNING}</h1>

      <ErrorCard chartMetaFormFields={formFieldMap} showErrorCard={formSubmittedCount > 0} />

      <SectionDivider />

      <h2 className="u-els-margin-bottom">{SectionTitle.PRIOR_HEALTH_STATUS}</h2>
      <ELSFlex className="u-flex-gutter-3x" wrap left>
        <ELSFlexItem w="1o2" md="1o1">
          {renderSelectField(FormField.CARE_STATUS_BEFORE_ADMISSION)}
        </ELSFlexItem>
        <ELSFlexItem w="1o2" md="1o1">
          {renderSelectField(FormField.LIVING_ARRANGEMENT_PRIOR_TO_ADMISSION)}
        </ELSFlexItem>
      </ELSFlex>
      <ELSFlex className="u-flex-gutter-3x" wrap left>
        <ELSFlexItem w="1o2" md="1o1">
          {renderNestedMultiselectField()}
        </ELSFlexItem>
      </ELSFlex>
      <ELSFlex className="u-flex-gutter-3x" wrap left>
        <ELSFlexItem w="1o2" md="1o1">
          {renderTextareaField(FormField.OTHER_NEEDS, { className: 'discharge-planning__textarea-half' })}
        </ELSFlexItem>
      </ELSFlex>

      <SectionDivider />

      <h2 className="u-els-margin-bottom">{SectionTitle.DISCHARGE_SUMMARY_AND_INSTRUCTIONS}</h2>
      <ELSFlex className="u-flex-gutter-3x" wrap left>
        <ELSFlexItem w="1o2" md="1o1">
          {renderSelectMultiselectField(FormField.SERVICES_ESTABLISHED_AFTER_DISCHARGE, `${FormField.SERVICES_ESTABLISHED_AFTER_DISCHARGE}-${multiselectResetKey}`)}
          <div className="discharge-planning__other-input">{renderOtherTextInput(FormField.SERVICES_ESTABLISHED_AFTER_DISCHARGE, FormField.SERVICES_ESTABLISHED_OTHER)}</div>
        </ELSFlexItem>
        <ELSFlexItem w="1o2" md="1o1">
          {renderSelectField(FormField.LIVING_ARRANGEMENT_ON_DISCHARGE)}
          <div className="discharge-planning__other-input">{renderOtherTextInput(FormField.LIVING_ARRANGEMENT_ON_DISCHARGE, FormField.LIVING_ARRANGEMENT_ON_DISCHARGE_OTHER)}</div>
        </ELSFlexItem>
      </ELSFlex>
      <ELSFlex className="u-flex-gutter-3x" wrap left>
        <ELSFlexItem w="1o2" md="1o1">
          {renderSelectMultiselectField(FormField.PHYSICAL_CHALLENGES_AT_HOME, `${FormField.PHYSICAL_CHALLENGES_AT_HOME}-${multiselectResetKey}`)}
          <div className="discharge-planning__other-input">{renderOtherTextInput(FormField.PHYSICAL_CHALLENGES_AT_HOME, FormField.PHYSICAL_CHALLENGES_AT_HOME_OTHER)}</div>
        </ELSFlexItem>
        <ELSFlexItem w="1o2" md="1o1">
          {renderSelectField(FormField.TRANSPORTATION_HOME_FROM_HOSPITAL)}
          <div className="discharge-planning__other-input">
            {renderOtherTextInput(FormField.TRANSPORTATION_HOME_FROM_HOSPITAL, FormField.TRANSPORTATION_HOME_FROM_HOSPITAL_OTHER)}
          </div>
        </ELSFlexItem>
      </ELSFlex>

      <SectionDivider />

      <h2 className="u-els-margin-bottom">{SectionTitle.CAREGIVER_INFORMATION}</h2>
      <ELSFlex className="u-flex-gutter-3x" wrap left>
        <ELSFlexItem w="1o2" md="1o1">
          {renderSelectField(FormField.CAREGIVER_AT_HOME)}
          <div className="discharge-planning__other-input">{renderOtherTextInput(FormField.CAREGIVER_AT_HOME, FormField.CAREGIVER_AT_HOME_OTHER)}</div>
        </ELSFlexItem>
        <ELSFlexItem w="1o2" md="1o1">
          {renderSelectField(FormField.GIVE_DISCHARGE_INSTRUCTIONS_TO)}
          <div className="discharge-planning__other-input">{renderOtherTextInput(FormField.GIVE_DISCHARGE_INSTRUCTIONS_TO, FormField.GIVE_DISCHARGE_INSTRUCTIONS_TO_OTHER)}</div>
        </ELSFlexItem>
      </ELSFlex>
      <ELSFlex className="u-flex-gutter-3x" wrap left>
        <ELSFlexItem w="1o2" md="1o1">
          {renderNameField(FormField.CAREGIVER_AT_HOME, FormField.CAREGIVER_NAME)}
        </ELSFlexItem>
        <ELSFlexItem w="1o2" md="1o1">
          {renderNameField(FormField.GIVE_DISCHARGE_INSTRUCTIONS_TO, FormField.DISCHARGE_INSTRUCTIONS_NAME)}
        </ELSFlexItem>
      </ELSFlex>
      <ELSFlex className="u-flex-gutter-3x" wrap left>
        <ELSFlexItem w="1o2" md="1o1">
          {renderSelectMultiselectField(FormField.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL, `${FormField.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL}-${multiselectResetKey}`)}
          <div className="discharge-planning__other-input">
            {renderOtherTextInput(FormField.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL, FormField.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL_OTHER)}
          </div>
        </ELSFlexItem>
      </ELSFlex>
      <ELSFlex className="u-flex-gutter-3x" wrap left>
        <ELSFlexItem w="1o2" md="1o1">
          {renderTextareaField(FormField.OTHER_MOTIVATIONAL_OBSERVATIONS, { className: 'discharge-planning__textarea-half' })}
        </ELSFlexItem>
      </ELSFlex>

      <ChartPermission>{({ allowSaveFragment }) => renderChartActions(allowSaveFragment)}</ChartPermission>
    </div>
  );
};

DischargePlanningView.displayName = 'DischargePlanningView';
export default DischargePlanningView;
